import React, { useCallback } from "react";
import { connect } from "react-redux";

import clsx from "clsx";
import { bindActionCreators, Dispatch } from "redux";

import { DataTableDef, SelectionMode, Boolean } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { InlineEditProvider, InlineEdit } from "ui/components";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { IChangeToken } from "../../shared/types/Token.types";
import { translationMessagesActions } from "../TranslationMessages.actions";
import { ITranslationMessageRow, ITranslationMessagesManagerState, IChangeText } from "../TranslationMessages.state";

import styles from "./TranslationMessagesGrid.module.scss";
import { TranslationMessagesTokenValueItem } from "./TranslationMessagesTokenValueItem";

export interface ITranslationMessagesGridProps {
  showPreviewText?: boolean;
  translations: ITranslationMessageRow[];
  selected: ITranslationMessageRow[];
  isSearched: boolean;
  changeTranslation: (change: IChangeText) => void;
  changeTokenValue: (change: IChangeToken) => void;
  selectTranslations: (selected: ITranslationMessageRow[]) => void;
}

export const TranslationMessagesManagerGrid: React.FC<ITranslationMessagesGridProps> = ({
  showPreviewText,
  translations,
  selected,
  isSearched,
  changeTranslation,
  changeTokenValue,
  selectTranslations
}) => {
  const localizedValues = useLocalizedValues(() => {
    return {
      messageId: displayMessages.property("messageId"),
      sourceText: displayMessages.property("sourceText"),
      translatedText: displayMessages.property("translatedText"),
      textPreviewTokenValues: displayMessages.property("textPreviewTokenValues"),
      textPreview: displayMessages.property("textPreview"),
      translatedBy: displayMessages.property("translatedBy"),
      parent: displayMessages.property("parent"),
      refineSearch: commonMessages.info("refineSearch"),
      startSearch: commonMessages.info("startSearch")
    };
  });

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const keySelector = useCallback((row: ITranslationMessageRow) => row.data.messageId, []);

  return (
    <InlineEditProvider>
      <DataTableDef<ITranslationMessageRow>
        className={clsx(styles.base, showPreviewText ? styles.showTextPreview : styles.notShowTextPreview)}
        data={translations}
        emptyText={isSearched ? localizedValues.refineSearch : localizedValues.startSearch}
        keySelector={keySelector}
        onSelectionChanged={selectTranslations}
        selected={selected}
        selectionMode={SelectionMode.multiple}
      >
        <DataTableDef.Column<ITranslationMessageRow> id="column1" key="1" title={localizedValues.messageId} valueSelector={r => r.data.messageId} sortable>
          {row => <p className={styles.wrapText}>{row.data.messageId}</p>}
        </DataTableDef.Column>

        <DataTableDef.Column<ITranslationMessageRow> id="column2" key="2" title={localizedValues.sourceText} valueSelector={r => r.data.sourceText} sortable>
          {row => <p className={styles.wrapText}>{row.data.sourceText}</p>}
        </DataTableDef.Column>

        <DataTableDef.Column<ITranslationMessageRow> id="column3" key="3" title={localizedValues.translatedText} valueSelector={r => r.data.translatedText} sortable>
          {row => (
            <InlineEdit<ITranslationMessageRow>
              initialValue={row.data.translatedText}
              originalValue={row.originalData && row.originalData.translatedText}
              onValueChanged={val => changeTranslation({ text: val, index: row.index })}
              isChanged={row.isTranslationChanged}
              validationIssues={row.validationIssues}
              className={styles.inlineEdit}
              canEdit={() => roles.LocalizationManager || roles.PlexLocalizationManager}
              context={row}
            >
              <p className={styles.wrapText}>{row.data.translatedText}</p>
            </InlineEdit>
          )}
        </DataTableDef.Column>

        {!!showPreviewText && (
          <DataTableDef.Column<ITranslationMessageRow> id="column4" key="4" title={localizedValues.textPreviewTokenValues}>
            {row => (
              <div className={styles.groupTokens}>
                {row.data.tokens.map((item, index) => {
                  return <TranslationMessagesTokenValueItem key={index} token={item} rowIndex={row.index} tokenIndex={index} changeTokenValue={changeTokenValue} />;
                })}
              </div>
            )}
          </DataTableDef.Column>
        )}

        {!!showPreviewText && (
          <DataTableDef.Column<ITranslationMessageRow> id="column5" key="5" title={localizedValues.textPreview} valueSelector={r => r.data.textPreview} sortable>
            {row => <p className={styles.wrapText}>{row.data.textPreview}</p>}
          </DataTableDef.Column>
        )}

        <DataTableDef.Column<ITranslationMessageRow>
          id="column7"
          key="7"
          className={styles.parentFlag}
          title={localizedValues.parent}
          valueSelector={r => r.data.isParentMessage}
          sortable
        >
          {row => <Boolean value={row.data.isParentMessage} />}
        </DataTableDef.Column>
      </DataTableDef>
    </InlineEditProvider>
  );
};

/* istanbul ignore next */
export default connect(
  (state: { translationMessagesManager: ITranslationMessagesManagerState }) => {
    const filter = state.translationMessagesManager.filter;
    const grid = state.translationMessagesManager.grid;
    return {
      showPreviewText: filter.lastData && filter.lastData.isCheckedShowPreviewText,
      translations: grid.gridData,
      selected: grid.selected,
      isSearched: grid.isSearched
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        changeTranslation: translationMessagesActions.changeTranslation,
        changeTokenValue: translationMessagesActions.changeTokenValue,
        selectTranslations: translationMessagesActions.selectTranslations
      },
      dispatch
    );
  }
)(TranslationMessagesManagerGrid);
